<template>
  <v-card color="card" class="elevation-2 rounded-lg">
    <v-sheet color="primary">
      <v-card-title class="d-flex pa-2">
        <span class="mr-auto text-h6 ml-2 white--text">
          {{ getLabel('external_access') }}
        </span>

        <div class="">
          <v-switch
            v-model="hasExternalAccess"
            class="mt-0"
            color="white"
            dense
            inset
            hide-details
            @change="enableExternAccess"
          />
        </div>
        <!-- </div> -->
      </v-card-title>
    </v-sheet>

    <v-form v-if="hasExternalAccess" ref="externalAccessForm">
      <v-row class="container">
        <v-col cols="4">
          <v-text-field v-model="externalAccess.company" v-bind="externalAccessFieldsProperties('company')" />
        </v-col>
        <v-col cols="4">
          <!-- ExternalAccessStartDate Field -->
          <v-menu
            ref="externalStartDateMenu"
            :close-on-content-click="false"
            :return-value.sync="externalAccess.externalAccessStartDate"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on }">
              <v-text-field
                v-model="externalAccess.externalAccessStartDate"
                v-bind="externalAccessFieldsProperties('externalAccessStartDate')"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-on="on"
              />
            </template>
            <v-date-picker v-model="externalAccess.externalAccessStartDate" no-title scrollable color="primary">
              <v-spacer />
              <v-btn text color="primary" @click="menu = false">
                Cancel
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.externalStartDateMenu.save(externalAccess.externalAccessStartDate)"
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>

        <v-col cols="4">
          <!-- ExternalAccessEndDate Field -->
          <v-menu
            ref="externalEndDateMenu"
            :close-on-content-click="false"
            :return-value.sync="externalAccess.externalAccessEndDate"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on }">
              <!-- v-bind="contactFieldsProperties('birth_date')" -->
              <v-text-field
                v-model="externalAccess.externalAccessEndDate"
                v-bind="externalAccessFieldsProperties('externalAccessEndDate')"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-on="on"
              />
            </template>
            <v-date-picker v-model="externalAccess.externalAccessEndDate" no-title scrollable color="primary">
              <v-spacer />
              <v-btn text color="primary" @click="menu = false">
                Cancel
              </v-btn>
              <v-btn text color="primary" @click="$refs.externalEndDateMenu.save(externalAccess.externalAccessEndDate)">
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
    </v-form>
    <div v-if="externalAccessChanged">
      <v-divider />
      <v-card-text class="d-flex justify-end">
        <!-- <v-btn color="warning" @click="resetForm()">
          <v-icon class="mr-3">mdi-broom</v-icon>
          {{ getLabel("clean") }}
        </v-btn> -->
        <v-btn class="ml-4" color="success" @click="saveExternalAccess()">
          <v-icon class="mr-3">mdi-content-save</v-icon>
          {{ getLabel("save") }}
        </v-btn>
      </v-card-text>
    </div>
  </v-card>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { useMutation } from '@vue/apollo-composable';

import { isEqual } from 'lodash';

import { getGraphqlSchema } from '@/graphql/index';
import { useMainStore } from '@/store/index';
import { getLabel, EventBus } from '@/utils/index';

import { externalAccessFieldsProperties } from '@/utils/forms';

export default {
  components: {},
  setup (props, context) {
    const store = useMainStore();

    const externalAccess = ref(null);

    const collab = ref(store.collaborator);

    if (collab.value.externalAccess) {
      externalAccess.value = JSON.parse(JSON.stringify(collab.value.externalAccess));
    }

    const hasExternalAccess = ref(!!collab.value.externalAccess);

    const externalAccessChanged = computed(() => {
      if (hasExternalAccess.value !== !!collab.value.externalAccess) {
        return true;
      }

      return !isEqual(collab.value.externalAccess, externalAccess.value);
    });

    const errors = ref([]);

    const successes = ref([]);

    const resetForm = () => {
      context.refs.externalAccessForm.reset();
      errors.value = [];
      successes.value = [];
    };

    // Update Collaborator ExternalAccess
    const { mutate: mutationUpdateExternalAccess, onError: errorUpdateExternalAccess, onDone: doneUpdateExternalAccess } = useMutation(
      getGraphqlSchema('updateCollaboratorExternalAccess'),
      () => ({
        variables:
        {
          uuid: collab.value.uuid,
          externalAccess: externalAccess.value
        }
      })
    );

    errorUpdateExternalAccess(() => {
      store.loading = false;

      EventBus.$emit('show-notifications', [{
        message: getLabel('error_updating_external_access'),
        type: 'danger'
      }]);
    });

    doneUpdateExternalAccess(result => {
      store.collaborator = { ...store.collaborator, ...result.data.updateCollaborator };

      collab.value = JSON.parse(JSON.stringify({ ...store.collaborator, ...result.data.updateCollaborator }));

      externalAccess.value = result.data.updateCollaborator.externalAccess;
      EventBus.$emit('show-notifications', [{
        message: getLabel('updated_external_access'),
        type: 'success'
      }]);

      store.loading = false;
    });

    const enableExternAccess = newValue => {
      // When switch is turned off
      if (!newValue) {
        externalAccess.value = null;
      } else {
        externalAccess.value = collab.value.externalAccess ? JSON.parse(JSON.stringify(collab.value.externalAccess)) : {
          company: null,
          externalAccessStartDate: null,
          externalAccessEndDate: null
        };
      }
    };

    const validation = () => externalAccess.value === null || context.refs.externalAccessForm.validate();

    const saveExternalAccess = () => {
      if (validation()) {
        store.loading = true;
        mutationUpdateExternalAccess();
      }
    };

    return { getLabel, externalAccessFieldsProperties, collab, resetForm, hasExternalAccess, externalAccess, externalAccessChanged, enableExternAccess, saveExternalAccess };
  }
};
</script>
